import React, { useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Bold, Italic, Underline, List, ListOrdered, Link, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder = '', minHeight = '200px' }) => {
  const editorRef = useRef(null);
  const hasInitialized = useRef(false);
  const lastValueFromParent = useRef(value || '');

  // Ref callback: sets innerHTML on mount
  const setEditorRef = useCallback((node) => {
    editorRef.current = node;
    if (node && !hasInitialized.current) {
      hasInitialized.current = true;
      node.innerHTML = value || '';
    }
  }, [value]);

  // Handle external value changes (templates, AI, translate) - NOT typing
  // We use a MutationObserver-free approach: check on each render
  if (editorRef.current && value !== lastValueFromParent.current) {
    // External change detected
    lastValueFromParent.current = value || '';
    // Use requestAnimationFrame to avoid setting innerHTML during render
    const node = editorRef.current;
    const newVal = value || '';
    requestAnimationFrame(() => {
      if (node && node.innerHTML !== newVal) {
        node.innerHTML = newVal;
      }
    });
  }

  const execCommand = useCallback((command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    if (editorRef.current && onChange) {
      const html = editorRef.current.innerHTML;
      lastValueFromParent.current = html;
      onChange(html);
    }
  }, [onChange]);

  const handleInput = useCallback(() => {
    if (editorRef.current && onChange) {
      const html = editorRef.current.innerHTML;
      lastValueFromParent.current = html;
      onChange(html);
    }
  }, [onChange]);

  const handleLink = () => {
    const url = prompt('URL:');
    if (url) execCommand('createLink', url);
  };

  return (
    <div className="border rounded-md overflow-hidden" data-testid="rich-text-editor">
      <div className="flex items-center gap-0.5 p-1.5 border-b bg-slate-50 flex-wrap">
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => execCommand('bold')}><Bold className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => execCommand('italic')}><Italic className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => execCommand('underline')}><Underline className="h-3.5 w-3.5" /></Button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => execCommand('insertUnorderedList')}><List className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => execCommand('insertOrderedList')}><ListOrdered className="h-3.5 w-3.5" /></Button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => execCommand('justifyLeft')}><AlignLeft className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => execCommand('justifyCenter')}><AlignCenter className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => execCommand('justifyRight')}><AlignRight className="h-3.5 w-3.5" /></Button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleLink}><Link className="h-3.5 w-3.5" /></Button>
      </div>
      <div
        ref={setEditorRef}
        contentEditable
        className="p-3 focus:outline-none prose prose-sm max-w-none"
        style={{ minHeight }}
        onInput={handleInput}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;
